import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { USE_MOCK, surveyApi } from '@/api/client';
import { MOCK_FUNDAMENTALS, MOCK_PERSONALIZADAS } from '@/data/mockSurveys';
import type { Survey, CustomSurvey } from '@/types/survey.types';
import type { SurveyItem, CreateSurveyResponse } from '@/api/types';
import type { SurveyForm } from '@/views/AdminView/SurveyBuilderView/types';
import { toCreateSurveyRequest } from '@/views/AdminView/SurveyBuilderView/hooks/useCreateSurvey';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useUserContext } from '@/contexts/UserContext';

/**
 * P4-7 — Identidad que se manda como `?userId=` en las escrituras de encuestas, para que el
 * SurveyService pueda registrar `createdBy`. En producción manda el oid de MSAL; en local, el
 * GUID del simulador. Ver la nota del endpoint en `api/client.ts`.
 */
function useWriterId(): string | undefined {
  const currentUser = useCurrentUser();
  const { demoUser } = useUserContext();
  return currentUser?.id || demoUser.userId || undefined;
}

export function adaptSurvey(s: SurveyItem): Survey {
  return {
    id: s.id,
    name: s.name,
    description: s.description,
    questions: s.questions,
    status: s.status,
    audience: s.audience,
    ouIds: s.ouIds,
    // D-3: la frecuencia se deriva de `activeDays` al pintar (ver surveyFrequency.ts). Antes
    // aquí se fabricaba el literal 'Por reunión' para TODAS las encuestas por igual.
    activeDays: s.activeDays,
    responses: s.responses,
    period: s.period,
    periodStart: s.periodStart,
    periodEnd: s.periodEnd,
    category: s.category,
    createdBy: s.createdBy,
  };
}

export function useFundamentalSurveys() {
  return useQuery<Survey[]>({
    queryKey: ['surveys', 'fundamentals'],
    queryFn: async () => {
      if (USE_MOCK) return MOCK_FUNDAMENTALS;
      const data = await surveyApi.getSurveys();
      return data.filter(s => s.status !== 'cancelled' && (s.category === 'general' || !s.category)).map(adaptSurvey);
    },
  });
}

export function useCustomSurveys() {
  return useQuery<CustomSurvey[]>({
    queryKey: ['surveys', 'custom'],
    queryFn: async () => {
      if (USE_MOCK) return MOCK_PERSONALIZADAS;
      const data = await surveyApi.getSurveys();
      return data.filter(s => s.status !== 'cancelled' && s.category === 'personalizada').map(s => ({
        id: s.id,
        name: s.name,
        description: s.description,
        createdBy: s.createdBy ?? '',
        status: s.status,
        audience: s.audience,
        ouIds: s.ouIds,
        activeDays: s.activeDays,
        audienceType: s.audienceType as 'meeting' | 'people' | 'ou' | undefined,
        questions: s.questions,
        responses: s.responses,
        period: s.period,
        periodStart: s.periodStart,
        periodEnd: s.periodEnd,
      }));
    },
  });
}

export function useDeletedSurveys() {
  return useQuery<import('@/api/types').SurveyItem[]>({
    queryKey: ['surveys', 'deleted'],
    queryFn: async () => {
      if (USE_MOCK) return [];
      return surveyApi.getDeletedSurveys();
    },
  });
}

export function useDeleteSurvey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => {
      if (USE_MOCK) {
        if (import.meta.env.DEV) console.log('[mock] deleteSurvey', id);
        return Promise.resolve();
      }
      return surveyApi.deleteSurvey(id);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['surveys'] });
      void qc.invalidateQueries({ queryKey: ['surveys', 'deleted'] });
    },
  });
}

export function useRestoreSurvey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => {
      if (USE_MOCK) { console.log('[MOCK] restoreSurvey', id); return Promise.resolve({} as CreateSurveyResponse); }
      return surveyApi.restoreSurvey(id);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['surveys'] });
      void qc.invalidateQueries({ queryKey: ['surveys', 'deleted'] });
    },
  });
}

export function usePermanentDeleteSurvey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => {
      if (USE_MOCK) {
        if (import.meta.env.DEV) console.log('[mock] permanentDeleteSurvey', id);
        return Promise.resolve();
      }
      return surveyApi.permanentDeleteSurvey(id);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['surveys'] });
      void qc.invalidateQueries({ queryKey: ['surveys', 'deleted'] });
    },
  });
}

export function usePatchSurveyStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, action }: { id: string; action: 'activate' | 'pause' }) => {
      if (USE_MOCK) { console.log('[MOCK] patchSurveyStatus', id, action); return Promise.resolve({} as CreateSurveyResponse); }
      return surveyApi.patchSurveyStatus(id, action);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['surveys'] }),
  });
}

export function useUpdateSurvey() {
  const qc = useQueryClient();
  const writerId = useWriterId();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: Parameters<typeof surveyApi.updateSurvey>[1] }) => {
      if (USE_MOCK) { console.log('[MOCK] updateSurvey', id); return Promise.resolve({} as CreateSurveyResponse); }
      return surveyApi.updateSurvey(id, body, writerId);
    },
    onSuccess: (_, { id }) => {
      void qc.invalidateQueries({ queryKey: ['surveys'] });
      // Evict the cached individual survey so the next edit-open always fetches fresh data.
      void qc.invalidateQueries({ queryKey: ['survey', id] });
    },
  });
}

export function useDuplicateSurvey() {
  const qc = useQueryClient();
  const writerId = useWriterId();
  return useMutation({
    mutationFn: (id: string) => {
      if (USE_MOCK) {
        if (import.meta.env.DEV) console.log('[mock] duplicateSurvey', id);
        return Promise.resolve({ id: crypto.randomUUID(), name: 'Copia', status: 'pending_approval', version: 1, createdBy: 'demo', createdAt: new Date().toISOString() } as CreateSurveyResponse);
      }
      return surveyApi.duplicateSurvey(id, writerId);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['surveys'] });
    },
  });
}

export function useSaveAsTemplate() {
  const qc = useQueryClient();
  const writerId = useWriterId();
  return useMutation({
    mutationFn: (id: string) => {
      if (USE_MOCK) { console.log('[MOCK] saveAsTemplate', id); return Promise.resolve({} as CreateSurveyResponse); }
      return surveyApi.saveAsTemplate(id, writerId);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['surveys', 'templates'] }),
  });
}

export function useRecalculateResponseCounts() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => {
      if (USE_MOCK) return Promise.resolve({ surveysUpdated: 0 });
      return surveyApi.recalculateResponseCounts();
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['surveys'] });
    },
  });
}

export function useCreateTemplate() {
  const qc = useQueryClient();
  return useMutation<CreateSurveyResponse, Error, SurveyForm>({
    mutationFn: (form: SurveyForm) => {
      if (USE_MOCK) {
        return Promise.resolve({
          id: crypto.randomUUID(),
          name: form.name,
          status: 'Draft',
          version: 1,
          createdBy: 'dev-user',
          createdAt: new Date().toISOString(),
        });
      }
      return surveyApi.createTemplate(toCreateSurveyRequest(form));
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['templates'] }),
  });
}
